import { z } from "zod";
import { isDisallowedWebhookHostname } from "./ipRangeCheck.js";
import { SUPPORTED_LOCALES } from "./locales.js";
/**
 * The language a message *to a person* should be written in. Carried on the
 * two requests that cause the API to send email, so the recipient reads the
 * OTP code and the creator reads the read receipt in the language they were
 * using — see `api/src/i18n`. Optional everywhere: an older client, or a
 * caller that isn't a browser, falls back to the operator's DEFAULT_LOCALE.
 *
 * A locale is the only thing about a person's client this app stores, and
 * only where an email has to be written later. It carries no more than the
 * `Accept-Language` header the same request already sent.
 */
export const localeSchema = z.enum(SUPPORTED_LOCALES);
export const expiryUnitSchema = z.enum(["hours", "days", "weeks"]);
export const expiresInSchema = z.object({
    value: z.number().int().positive(),
    unit: expiryUnitSchema,
});
const allowedEmailSchema = z.string().trim().toLowerCase().email().optional();
const webhookUrlSchema = z
    .string()
    .trim()
    .url()
    .refine((url) => url.startsWith("http://") || url.startsWith("https://"), {
    message: "webhookUrl must use http or https",
})
    .refine((url) => !isDisallowedWebhookHostname(new URL(url).hostname), {
    message: "webhookUrl must not target localhost or a private/internal address",
})
    .optional();
const webhookSecretSchema = z.string().trim().min(16).max(256).optional();
function requireWebhookSecretWithUrl(data) {
    return Boolean(data.webhookUrl) === Boolean(data.webhookSecret);
}
const WEBHOOK_PAIR_ISSUE = {
    message: "webhookUrl and webhookSecret must be set together",
    path: ["webhookSecret"],
};
export const createTextSecretSchema = z
    .object({
    ciphertext: z.string().min(1),
    expiresIn: expiresInSchema,
    oneTime: z.boolean(),
    allowedEmail: allowedEmailSchema,
    notifyEmail: allowedEmailSchema,
    webhookUrl: webhookUrlSchema,
    webhookSecret: webhookSecretSchema,
    // The creator's language, kept for the read receipt sent at view time.
    locale: localeSchema.optional(),
})
    .refine(requireWebhookSecretWithUrl, WEBHOOK_PAIR_ISSUE);
export const createFileSecretMetaSchema = z
    .object({
    expiresIn: expiresInSchema,
    oneTime: z.boolean(),
    allowedEmail: allowedEmailSchema,
    notifyEmail: allowedEmailSchema,
    encryptedMeta: z.string().min(1),
    webhookUrl: webhookUrlSchema,
    webhookSecret: webhookSecretSchema,
    // The creator's language, kept for the read receipt sent at view time.
    locale: localeSchema.optional(),
})
    .refine(requireWebhookSecretWithUrl, WEBHOOK_PAIR_ISSUE);
export const createSecretResponseSchema = z.object({
    id: z.string().min(1),
    expiresAt: z.number().int().positive(),
});
export const secretTypeSchema = z.enum(["text", "file"]);
export const secretMetaResponseSchema = z.object({
    exists: z.boolean(),
    type: secretTypeSchema.optional(),
    size: z.number().int().nonnegative().optional(),
    oneTime: z.boolean().optional(),
    requiresSpecificEmail: z.boolean().optional(),
    encryptedMeta: z.string().optional(),
    expiresAt: z.number().int().positive().optional(),
});
export const otpRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    // The recipient's language, used only to write the code email they are
    // about to receive. Not stored.
    locale: localeSchema.optional(),
});
export const otpRequestResponseSchema = z.object({
    sent: z.literal(true),
    cooldownSeconds: z.number().int().positive(),
});
export const otpVerifySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    code: z.string().regex(/^\d{4,8}$/),
});
/**
 * A one-shot permission to create exactly one secret, handed to a recipient
 * who has just passed the OTP gate. It exists for one situation: with
 * SSO_ENABLED the two creation routes are closed to anyone without an
 * account in the operator's identity provider, which leaves an external
 * recipient unable to answer the secret they were just sent.
 *
 * It is strictly narrower than a session. It authorizes one creation, it
 * expires on its own, and the secret it creates is addressed by the server
 * to `replyTo` — the person who sent the original — so it cannot be used to
 * send a secret to anyone else. It says nothing about the recipient beyond
 * the address they already proved they control, and it is not a credential
 * for anything else this API does.
 *
 * `replyTo` travels back to the browser because the reply composer has to
 * be able to say who the reply is going to. Only a request that has already
 * answered a code sent to the allowed address is ever told it.
 */
export const replyGrantSchema = z.object({
    token: z.string().min(1),
    replyTo: z.string().email(),
    expiresAt: z.number().int().positive(),
});
export const otpVerifyResponseSchema = z.object({
    retrievalToken: z.string().min(1),
    // Present only where the operator has turned replies on and the original
    // secret was created by a signed-in user there is an address to answer.
    replyGrant: replyGrantSchema.optional(),
});
export const otpErrorCodeSchema = z.enum([
    "email_rejected",
    "otp_incorrect",
    "otp_locked",
    "otp_expired_or_not_found",
    "cooldown_active",
    "rate_limited",
    "not_found",
]);
export const auditOutcomeSchema = z.enum([
    "email_rejected",
    "otp_requested",
    "otp_verified",
    "otp_failed",
    "viewed",
    // A retrieval turned away because this one-time secret was already claimed
    // by an earlier one — the gate was passed, the bytes were not handed over.
    "retrieval_blocked",
    "created",
    // A secret created through a reply grant rather than a session: the actor
    // is the OTP-verified recipient of an earlier secret, not an SSO user.
    "created_reply",
]);
export const auditLogEntrySchema = z.object({
    ts: z.number().int().positive(),
    secretId: z.string().min(1),
    email: z.string(),
    outcome: auditOutcomeSchema,
    ip: z.string(),
});
export const webhookEventSchema = z.enum(["created", "viewed", "expired"]);
export const webhookPayloadSchema = z.object({
    event: webhookEventSchema,
    secretId: z.string().min(1),
    timestamp: z.number().int().positive(),
});
export const authSessionResponseSchema = z.object({
    enabled: z.boolean(),
    authenticated: z.boolean(),
    email: z.string().email().optional(),
});
/**
 * Browser-side retrieval telemetry: the handful of things the server can't
 * see for itself (whether the link still had its key, whether decryption
 * worked, how long it took). Deliberately an *allowlist* — every field is a
 * bounded enum or a number, `.strict()` rejects anything extra, and there is
 * no free-text field anywhere, so a future caller cannot smuggle a URL, a
 * passphrase, a filename, or a stack trace through it. Widening this type is
 * the moment to re-read AGENTS.md's invariants.
 */
export const browserEventNameSchema = z.enum(["link_opened", "meta_loaded", "secret_retrieved"]);
export const browserEventOutcomeSchema = z.enum([
    "ok",
    // link_opened: the URL arrived with no fragment — usually a truncated
    // copy/paste, or a reload after the key was stripped from the address bar.
    "missing_key",
    // meta_loaded: the id is gone (expired, or a one-time secret already read).
    // secret_retrieved: the download was refused because the one-time secret
    // had already been claimed by another retrieval.
    "not_found",
    // meta_loaded: the fragment key doesn't decrypt this secret's metadata.
    "key_mismatch",
    // secret_retrieved: ciphertext arrived but would not decrypt.
    "decrypt_failed",
    // Either step: the API could not be reached, or answered unexpectedly.
    "network_error",
]);
export const browserEventSchema = z
    .object({
    event: browserEventNameSchema,
    outcome: browserEventOutcomeSchema,
    secretType: z.enum(["text", "file"]).optional(),
    // Wall-clock for the step, capped so a bad clock can't skew a histogram.
    durationMs: z.number().int().nonnegative().max(600000).optional(),
})
    .strict();
//# sourceMappingURL=schemas.js.map